import { useCallback, useEffect, useState } from 'react'
import { OvbResolveType, useChildrenQuery, useUpdateReservedResourcesMutation } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { RRBulkEditFormData } from './reserved-resources-bulk-edit-form.hook'
import { ReservedResourceF } from '../../../types/reservation-detail.types'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { useTranslation } from 'react-i18next'
import { reservedResourceToReservedResourceMultiInput } from './reserved-resources-bulk-edit-utils'
import { includeErrorCode } from 'src/app/hooks/error-message/apollo-error-message'
import { CoreErrorCode } from 'src/app/hooks/error-message/error-handler.backend-types'
import { useReservedResourceDailyPricesUtils } from 'src/app/hooks/update-reserved-resource/edit-reserved-resource-dates.hook'
import useUpdateReservedResourceHook from 'src/app/hooks/update-reserved-resource/update-reserved-resource.hook'

export default function useReservedResourcesBulkEditCRUD(propertyId: string, onRRUpdated?: () => void) {
  const { id: tenantId } = useTenant()
  const { t } = useTranslation()
  const localizeString = useLocalizeString()
  const showError = useErrorSnackBar()
  const showSuccess = useSuccessSnackBar()

  const { getUpdatedDailyPrices, loading: priceUtilsLoading, errors: priceUtilsErrors } = useReservedResourceDailyPricesUtils(tenantId)

  useEffect(() => {
    if (priceUtilsErrors) showError(priceUtilsErrors)
  }, [priceUtilsErrors])

  const [loading, setLoading] = useState(false)

  const [children, setChildren] = useState<{ childId: string; description: string; noOccupancy: boolean }[]>([])
  const { loading: loadingChildren } = useChildrenQuery({
    variables: {
      filter: {
        propertyId: {
          eq: propertyId,
        },
        tenantId: {
          eq: tenantId,
        },
      },
    },
    onCompleted: (data) => {
      if (data.children) {
        setChildren(
          (data.children ?? []).map((child) => {
            return {
              childId: child.id,
              description: localizeString(child.taxonomy.description),
              noOccupancy: child.noOccupancy,
            }
          }),
        )
      }
    },
  })

  const { updateManyReservedResourceMutation, updateRRMutationLoading } = useUpdateReservedResourceHook({ refetchQueries: ['ReservationDetail'] })

  const updateRRs = useCallback(
    async (reservedResourcesToUpdate: ReservedResourceF[], data: RRBulkEditFormData, overbooking: OvbResolveType) => {
      setLoading(true)

      let stopUpdate = false
      const reservedResourceMultiInput = reservedResourcesToUpdate.map(async (rr) => {
        const input = reservedResourceToReservedResourceMultiInput(tenantId, rr)
        let childOccupancies = rr.childOccupancies?.map((c) => {
          return {
            childId: c.child.id,
            quantity: c.quantity,
          }
        })
        let children = childOccupancies?.reduce((prev, curr) => prev + curr.quantity, 0) ?? 0

        if (data.children) {
          children = data.children.reduce((prev, curr) => prev + curr.quantity, 0)
          childOccupancies = data.children?.map((c) => {
            return {
              childId: c.childId,
              quantity: c.quantity,
            }
          })
        } else if (data.adults) {
          children = 0
          childOccupancies = undefined
        }

        let updatedManualDailyPrices: { day: Date; price: number }[] = []
        if (!data.pricePerNight && data.pricePerNight !== 0 && rr.manualDailyPrices) {
          const response = await getUpdatedDailyPrices(
            rr.propertyId,
            rr.resourceOccupancies[rr.resourceOccupancies.length - 1].reservedResourceType.id,
            rr.rate.id,
            data.checkIn ?? rr.start,
            data.checkOut ?? rr.end,
            rr.start,
            rr.end,
            data.chargeCategoryId ?? rr.chargeCategory.id,
            rr.adults,
            rr.childOccupancies?.map((co) => ({ childId: co.child.id, quantity: co.quantity })),
            rr.manualPrice,
            rr.manualDailyPrices,
            rr.dailyPricesStored?.dailyPrices,
          )
          if (response) updatedManualDailyPrices = response
          else stopUpdate = true
        }

        return {
          ...input,
          reservedResource: {
            ...input.reservedResource,
            adults: data.adults ?? rr.adults,
            chargeCategoryId: data.chargeCategoryId ?? rr.chargeCategory.id,
            childOccupancies: childOccupancies,
            children: children,
            manualDailyPrices: updatedManualDailyPrices && updatedManualDailyPrices.length > 0 ? updatedManualDailyPrices : null,
            manualPrice: updatedManualDailyPrices && updatedManualDailyPrices.length > 0 ? null : data.pricePerNight ?? rr.manualPrice,
            rateId: data.rateId ? data.rateId : rr.rate.id,
            resourceOccupancies: rr.resourceOccupancies.map((ro) => {
              return {
                end: data.checkOut ?? ro.end,
                resourceId: ro.resource?.id,
                resourceTypeId: ro.reservedResourceType.id,
                start: data.checkIn ?? ro.start,
              }
            }),
          },
        }
      })

      if (!stopUpdate) {
        try {
          const input = await Promise.all(reservedResourceMultiInput)
          const result = await updateManyReservedResourceMutation({ input: input })
          if (result.result === 'SUCCESS') {
            showSuccess(t('pms.booking.reservation.rr_bulk_edit_dialog.form.success_message'))
            onRRUpdated?.()
          }
          if (result.result === 'ERROR' && result.error) {
            if (includeErrorCode(result.error, CoreErrorCode.OVERBOOKING_NOT_ALLOWED)) showError(t('pms.booking.reservation.reserved_resource_edit.overbooking_issue'))
            else showError(result.error)
          }
        } catch (e) {
          showError(e)
        }
      } else {
        showError(t('commons.generic_error_occurred'))
      }

      setLoading(false)
    },
    [updateManyReservedResourceMutation, loading, setLoading],
  )

  useEffect(() => {
    setLoading(loadingChildren || updateRRMutationLoading || priceUtilsLoading)
  }, [loadingChildren, updateRRMutationLoading, updateRRs, priceUtilsLoading])

  return { loading, children, updateRRs }
}
